/**
 * BannersTab Component
 * Manages news/banner items for the mobile app Journal page
 */

import { useRef, useState, ChangeEvent } from 'react';
import { Loader2, Plus, Trash2, X, LayoutDashboard, GripVertical, Youtube, Link as LinkIcon } from 'lucide-react';
import { useBannerManagement } from '../hooks';
import { cn } from '../../../utils/responsive';

interface BannersTabProps {
    isMobileOrTablet: boolean;
    savingPage: string | null;
    onSave: () => Promise<void>;
}

export function BannersTab({ isMobileOrTablet, savingPage, onSave }: BannersTabProps) {
    const bannerUploadInputRef = useRef<HTMLInputElement>(null);

    const {
        banners,
        isLoadingBanners,
        bannersChanged,
        youtubeModalOpen,
        setYoutubeModalOpen,
        youtubeModalBannerIndex,
        setYoutubeModalBannerIndex,
        youtubeUrlInput,
        setYoutubeUrlInput,
        handleAddBannerFiles,
        handleBannerTextChange,
        handleBannerRemove,
        handleBannerReorder,
        handleAddYouTubeVideo,
        handleRemoveMediaFromBanner,
        handleReorderMediaInBanner,
        handleAddMediaToBanner,
        handleActionLinkAdd,
        handleActionLinkChange,
        handleActionLinkRemove,
        handleActionLinkImageUpload,
        saveBanners,
    } = useBannerManagement();

    // Drag and drop state
    const [draggedBannerIndex, setDraggedBannerIndex] = useState<number | null>(null);
    const [draggedMediaIndex, setDraggedMediaIndex] = useState<{ bannerIdx: number; mediaIdx: number } | null>(null);

    if (isLoadingBanners) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-yellow-500 dark:text-yellow-600" />
                <span className="ml-3 text-gray-600 dark:text-gray-400">กำลังโหลด banners...</span>
            </div>
        );
    }

    const activeBanners = banners.filter(b => !b.isDeleted);

    const handleSave = async () => {
        await saveBanners();
        onSave?.();
    };

    return (
        <>
            <h2 className="text-base lg:text-lg font-semibold text-stone-900 dark:text-gray-100">
                News & Banner Management
            </h2>
            <p className="mt-1 text-xs lg:text-sm text-stone-600 dark:text-gray-400">
                อัปโหลดข่าวสารที่มีภาพ Banner และข้อความประกอบ จะแสดงบนหน้า Journal ในแอป
            </p>

            <div className="mt-4 lg:mt-6 space-y-4 lg:space-y-6">
                {/* Upload Button */}
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
                    <input
                        type="file"
                        multiple
                        accept="image/*,.svg"
                        ref={bannerUploadInputRef}
                        onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                            await handleAddBannerFiles(e.target.files ?? undefined);
                            if (bannerUploadInputRef.current) {
                                bannerUploadInputRef.current.value = '';
                            }
                        }}
                        className="hidden"
                    />
                    <button
                        type="button"
                        onClick={() => bannerUploadInputRef.current?.click()}
                        className={cn(
                            "inline-flex items-center justify-center gap-2",
                            "rounded-lg border border-stone-300 dark:border-gray-600 bg-white dark:bg-gray-800",
                            "px-4 lg:px-5 py-2.5 lg:py-3",
                            "text-sm lg:text-base font-medium text-stone-700 dark:text-gray-300",
                            "transition-all duration-200",
                            "hover:border-yellow-500 dark:hover:border-orange-500 hover:text-yellow-600 dark:hover:text-orange-500 hover:shadow-sm",
                            "active:scale-95 touch-target"
                        )}
                    >
                        <Plus className="h-4 w-4" />
                        <span>เพิ่ม Banner ข่าว</span>
                    </button>
                    <span className="text-xs lg:text-sm text-stone-500 dark:text-gray-400">
                        ลากเพื่อจัดลำดับ หากมีหลายข่าวจะเรียงตามอันดับนี้บนแอป
                    </span>
                </div>

                {/* Banner List */}
                {activeBanners.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-stone-300 dark:border-gray-600 bg-stone-50 dark:bg-gray-900 p-6 lg:p-8 text-center text-sm lg:text-base text-stone-500 dark:text-gray-400">
                        ยังไม่มีข่าวสาร โปรดเพิ่ม Banner เพื่อเริ่มต้น
                    </div>
                ) : (
                    <div className="space-y-6">
                        {activeBanners.map((banner, index) => {
                            const realIndex = banners.findIndex((b) => b === banner);

                            return (
                                <div
                                    key={banner.id || `new-${index}`}
                                    draggable
                                    onDragStart={() => setDraggedBannerIndex(realIndex)}
                                    onDragEnd={() => setDraggedBannerIndex(null)}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={() => {
                                        if (draggedBannerIndex !== null && draggedBannerIndex !== realIndex) {
                                            handleBannerReorder(draggedBannerIndex, realIndex);
                                        }
                                    }}
                                    className={cn(
                                        "relative rounded-2xl border-2 p-4 lg:p-6 transition-all",
                                        draggedBannerIndex === realIndex
                                            ? "border-yellow-500 dark:border-orange-500 bg-yellow-50 dark:bg-orange-900/20 shadow-lg"
                                            : "border-stone-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 hover:shadow-md"
                                    )}
                                >
                                    {/* Header */}
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <div className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-stone-100 dark:hover:bg-gray-700">
                                                <GripVertical className="h-5 w-5 text-stone-400" />
                                            </div>
                                            <span className="text-sm font-bold text-stone-600 dark:text-gray-300">
                                                #{index + 1}
                                            </span>
                                            {banner.id && !banner.id.startsWith('temp-') && (
                                                <span className="px-2 py-0.5 text-[10px] rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                                    Published
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleBannerRemove(realIndex)}
                                            className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 lg:gap-6">
                                        {/* Left: Media Section */}
                                        <div className="xl:col-span-5 space-y-3">
                                            <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider">
                                                รูปภาพ/วิดีโอ (สูงสุด 10 รายการ)
                                            </label>
                                            <div className="flex flex-wrap gap-2">
                                                {banner.media.map((media, mediaIdx) => (
                                                    <div
                                                        key={mediaIdx}
                                                        draggable
                                                        onDragStart={() => setDraggedMediaIndex({ bannerIdx: realIndex, mediaIdx })}
                                                        onDragEnd={() => setDraggedMediaIndex(null)}
                                                        onDragOver={(e) => e.preventDefault()}
                                                        onDrop={() => {
                                                            if (draggedMediaIndex && draggedMediaIndex.bannerIdx === realIndex && draggedMediaIndex.mediaIdx !== mediaIdx) {
                                                                handleReorderMediaInBanner(realIndex, draggedMediaIndex.mediaIdx, mediaIdx);
                                                            }
                                                        }}
                                                        className={cn(
                                                            "relative h-24 w-24 rounded-xl overflow-hidden border-2 group cursor-grab",
                                                            draggedMediaIndex?.bannerIdx === realIndex && draggedMediaIndex?.mediaIdx === mediaIdx
                                                                ? "border-yellow-500 dark:border-orange-500"
                                                                : "border-stone-200 dark:border-gray-700"
                                                        )}
                                                    >
                                                        <img
                                                            src={media.data}
                                                            alt=""
                                                            className="h-full w-full object-cover"
                                                        />
                                                        {media.type === 'video' && (
                                                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                                                <Youtube className="h-8 w-8 text-white" />
                                                            </div>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveMediaFromBanner(realIndex, mediaIdx)}
                                                            className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition"
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                ))}

                                                {banner.media.length < 10 && (
                                                    <>
                                                        {/* Add Image Button */}
                                                        <label className="flex h-24 w-24 flex-col items-center justify-center rounded-xl border-2 border-dashed border-stone-300 dark:border-gray-600 text-center text-stone-400 transition hover:border-yellow-500 hover:text-yellow-600 cursor-pointer group">
                                                            <Plus className="h-6 w-6 transition group-hover:scale-110" />
                                                            <span className="mt-1 text-[10px] font-medium">เพิ่มรูป</span>
                                                            <input
                                                                type="file"
                                                                multiple
                                                                accept="image/*,.svg"
                                                                className="hidden"
                                                                onChange={(e: ChangeEvent<HTMLInputElement>) => handleAddMediaToBanner(realIndex, e.target.files ?? undefined)}
                                                            />
                                                        </label>

                                                        {/* Add YouTube Button */}
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setYoutubeModalBannerIndex(realIndex);
                                                                setYoutubeUrlInput('');
                                                                setYoutubeModalOpen(true);
                                                            }}
                                                            className="flex h-24 w-24 flex-col items-center justify-center rounded-xl border-2 border-dashed border-red-300 dark:border-red-700 text-center text-red-400 transition hover:border-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 group"
                                                        >
                                                            <Youtube className="h-6 w-6 transition group-hover:scale-110" />
                                                            <span className="mt-1 text-[10px] font-medium">YouTube</span>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                            <p className="text-[10px] text-stone-400 italic">
                                                * รูปภาพ: แนะนำขนาด 16:9 | วิดีโอ: รองรับ YouTube URL
                                            </p>
                                        </div>

                                        {/* Right: Basic Info */}
                                        <div className="xl:col-span-7 space-y-5">
                                            <div className="grid grid-cols-1 gap-5">
                                                <div>
                                                    <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider block mb-1.5">
                                                        หัวข้อข่าว (Banner Title)
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={banner.title}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleBannerTextChange(realIndex, 'title', e.target.value)}
                                                        placeholder="กรอกหัวข้อสั้น ๆ เพื่อดึงดูดความสนใจ..."
                                                        className="w-full rounded-xl border border-stone-200 dark:border-gray-700 bg-stone-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50 dark:focus:ring-orange-500/50 transition-all placeholder:text-stone-400"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider block mb-1.5">
                                                        รายละเอียดข่าว (Content Body)
                                                    </label>
                                                    <textarea
                                                        value={banner.body}
                                                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleBannerTextChange(realIndex, 'body', e.target.value)}
                                                        rows={5}
                                                        placeholder="ใส่รายละเอียดเนื้อหาข่าวสารที่นี่..."
                                                        className="w-full rounded-xl border border-stone-200 dark:border-gray-700 bg-stone-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50 dark:focus:ring-orange-500/50 transition-all placeholder:text-stone-400"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider block mb-1.5">
                                                        ลิงก์หลัก (Primary Link - ถ้ามี)
                                                    </label>
                                                    <div className="relative flex items-center">
                                                        <input
                                                            type="url"
                                                            value={banner.linkUrl}
                                                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleBannerTextChange(realIndex, 'linkUrl', e.target.value)}
                                                            placeholder="https://example.com/more-info"
                                                            className="w-full rounded-xl border border-stone-200 dark:border-gray-700 bg-stone-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50 dark:focus:ring-orange-500/50 transition-all placeholder:text-stone-400"
                                                        />
                                                        <LayoutDashboard className="absolute left-3.5 h-4 w-4 text-stone-400" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Links Section */}
                                    <div className="pt-6 mt-6 border-t border-stone-100 dark:border-gray-700 space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <label className="text-xs font-bold text-stone-600 dark:text-gray-300 uppercase tracking-wider flex items-center gap-2">
                                                    <span>รูปภาพท้ายข่าวสาร (Action Links / Sponsors)</span>
                                                    <span className="normal-case font-normal text-stone-400">| แสดงด้านล่างเนื้อหา</span>
                                                </label>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleActionLinkAdd(realIndex)}
                                                className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-50 dark:bg-orange-500/10 text-xs font-bold text-yellow-600 dark:text-orange-400 hover:bg-yellow-100 dark:hover:bg-orange-500/20 transition-all shadow-sm"
                                            >
                                                <Plus className="h-3.5 w-3.5" />
                                                เพิ่ม Action Link
                                            </button>
                                        </div>

                                        {banner.actionLinks.filter(l => !l.isDeleted).length > 0 && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {banner.actionLinks.map((link, linkIdx) => {
                                                    if (link.isDeleted) return null;
                                                    return (
                                                        <div
                                                            key={link.id || `link-${linkIdx}`}
                                                            className="p-4 rounded-xl border border-stone-200 dark:border-gray-700 bg-stone-50 dark:bg-gray-900 space-y-3"
                                                        >
                                                            {/* Image Upload */}
                                                            <div className="relative aspect-[3/2] rounded-lg overflow-hidden border border-dashed border-stone-300 dark:border-gray-600 bg-white dark:bg-gray-800">
                                                                {link.image ? (
                                                                    <>
                                                                        <img src={link.image} alt="" className="w-full h-full object-cover" />
                                                                        <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition flex items-center justify-center gap-2">
                                                                            <label className="cursor-pointer px-2 py-1 bg-white/90 rounded text-[10px] font-bold text-gray-700 hover:bg-white">
                                                                                เปลี่ยน
                                                                                <input
                                                                                    type="file"
                                                                                    accept="image/*"
                                                                                    className="hidden"
                                                                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                                                        const file = e.target.files?.[0];
                                                                                        if (file) handleActionLinkImageUpload(realIndex, linkIdx, file);
                                                                                    }}
                                                                                />
                                                                            </label>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleActionLinkRemove(realIndex, linkIdx)}
                                                                                className="px-2 py-1 bg-red-500/90 rounded text-[10px] font-bold text-white hover:bg-red-600"
                                                                            >
                                                                                ลบ
                                                                            </button>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <label className="cursor-pointer w-full h-full flex flex-col items-center justify-center text-stone-400 hover:text-yellow-600 transition">
                                                                        <Plus className="h-8 w-8" />
                                                                        <span className="text-xs mt-1">อัปโหลดรูป</span>
                                                                        <input
                                                                            type="file"
                                                                            accept="image/*"
                                                                            className="hidden"
                                                                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                                                const file = e.target.files?.[0];
                                                                                if (file) handleActionLinkImageUpload(realIndex, linkIdx, file);
                                                                            }}
                                                                        />
                                                                    </label>
                                                                )}
                                                            </div>

                                                            {/* URL Input */}
                                                            <div className="relative">
                                                                <input
                                                                    type="url"
                                                                    value={link.url}
                                                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleActionLinkChange(realIndex, linkIdx, 'url', e.target.value)}
                                                                    placeholder="https://..."
                                                                    className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-stone-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                                                />
                                                                <LinkIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400" />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-stone-200 dark:border-gray-700">
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!bannersChanged || savingPage === 'banners'}
                        className={cn(
                            "inline-flex items-center justify-center gap-2",
                            "rounded-lg px-6 py-3 text-sm font-semibold",
                            "bg-yellow-500 hover:bg-yellow-600 text-white",
                            "transition-all duration-200 hover:scale-105",
                            "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100",
                            isMobileOrTablet && "w-full"
                        )}
                    >
                        {savingPage === 'banners' ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>กำลังบันทึก...</span>
                            </>
                        ) : (
                            'บันทึก Banners'
                        )}
                    </button>
                </div>
            </div>

            {/* YouTube Modal */}
            {youtubeModalOpen && youtubeModalBannerIndex !== null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 w-full max-w-md space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">เพิ่ม YouTube Video</h3>
                            <button
                                type="button"
                                onClick={() => setYoutubeModalOpen(false)}
                                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
                                YouTube URL
                            </label>
                            <input
                                type="url"
                                value={youtubeUrlInput}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setYoutubeUrlInput(e.target.value)}
                                placeholder="https://www.youtube.com/watch?v=..."
                                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                            />
                            <p className="text-xs text-gray-500 mt-2">
                                รองรับ: youtube.com/watch?v=..., youtu.be/..., youtube.com/shorts/...
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setYoutubeModalOpen(false)}
                                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                ยกเลิก
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (handleAddYouTubeVideo(youtubeModalBannerIndex, youtubeUrlInput)) {
                                        setYoutubeModalOpen(false);
                                    }
                                }}
                                className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white font-semibold hover:bg-red-600"
                            >
                                เพิ่มวิดีโอ
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
